import { useEffect, useRef } from "react";
import type { Dispatch, SetStateAction } from "react";

import type { IMultiAnalysisDataMark, IMultiAnalysisTimeRange } from "../types";
import { filterDataMarksInRange, isDateInRange, selectDataMarksInTimeRange } from "../utils/data-marks";

interface ITimePanelMarkSelectionOptions {
  dataMarks: IMultiAnalysisDataMark[];
  frameRange: IMultiAnalysisTimeRange;
  selectedDataMarks: IMultiAnalysisDataMark[];
  setSelectedDataMarks: Dispatch<SetStateAction<IMultiAnalysisDataMark[]>>;
  additivePlainClick?: boolean;
  singleSelection?: boolean;
}

export const useTimePanelMarkSelection = ({
  dataMarks,
  frameRange,
  selectedDataMarks,
  setSelectedDataMarks,
  additivePlainClick = false,
  singleSelection = false,
}: ITimePanelMarkSelectionOptions) => {
  const shiftAnchorRef = useRef<IMultiAnalysisDataMark | null>(null);

  useEffect(() => {
    setSelectedDataMarks(prev => {
      const synced = prev.filter(mark => dataMarks.some(item => item.key === mark.key));

      return synced.length === prev.length ? prev : synced;
    });
  }, [dataMarks, setSelectedDataMarks]);

  useEffect(() => {
    setSelectedDataMarks(prev => {
      const inFrame = filterDataMarksInRange(prev, frameRange);

      return inFrame.length === prev.length ? prev : inFrame;
    });
  }, [frameRange, setSelectedDataMarks]);

  useEffect(() => {
    shiftAnchorRef.current = null;
  }, [dataMarks]);

  useEffect(() => {
    if (shiftAnchorRef.current && !isDateInRange(shiftAnchorRef.current.date, frameRange)) {
      shiftAnchorRef.current = null;
    }
  }, [frameRange]);

  const handleMarkClick = (
    mark: IMultiAnalysisDataMark,
    event: { shiftKey: boolean; ctrlKey: boolean; metaKey: boolean },
  ) => {
    if (!isDateInRange(mark.date, frameRange)) {
      return;
    }

    if (singleSelection) {
      shiftAnchorRef.current = mark;
      const isOnlySelectedMark = selectedDataMarks.length === 1 && selectedDataMarks[0]?.key === mark.key;
      setSelectedDataMarks(isOnlySelectedMark ? [] : [mark]);
      if (isOnlySelectedMark) shiftAnchorRef.current = null;
      return;
    }

    if (event.shiftKey) {
      const anchor = shiftAnchorRef.current ?? selectedDataMarks[0] ?? mark;
      const nextAnchor = isDateInRange(anchor.date, frameRange) ? anchor : mark;
      shiftAnchorRef.current = nextAnchor;
      setSelectedDataMarks(filterDataMarksInRange(selectDataMarksInTimeRange(dataMarks, nextAnchor, mark), frameRange));

      return;
    }

    shiftAnchorRef.current = mark;

    if (event.ctrlKey || event.metaKey || additivePlainClick) {
      const exists = selectedDataMarks.some(item => item.key === mark.key);
      setSelectedDataMarks(
        exists ? selectedDataMarks.filter(item => item.key !== mark.key) : [...selectedDataMarks, mark],
      );

      return;
    }

    const isOnlySelectedMark = selectedDataMarks.length === 1 && selectedDataMarks[0]?.key === mark.key;

    if (isOnlySelectedMark) {
      setSelectedDataMarks([]);
      shiftAnchorRef.current = null;

      return;
    }

    setSelectedDataMarks([mark]);
  };

  return { handleMarkClick };
};
